///
/// EarthBrowser
///
/// Created by Matt Giger on December 15, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { logger } from '@/utils/LogDispatcher'
import { GeoPackageLoader, type ProgressCallback } from './GeoPackageLoader'
import { GeoPackageCatalogTable } from '@/earthbrowser/database/tables/GeoPackageCatalogTable'
import type { GeoPackageCatalog } from '@shared-types/geopackage-catalog'
import { getFileSystemAccessManager } from '@/earthbrowser/filesystem/FileSystemAccessManager'
import type { ExternalGeoPackageFile } from '@/earthbrowser/filesystem/types'
import type { GeoPackageDatabase } from './GeoPackageDatabase'

export interface GeoPackageConfig {
	id: string
	filename: string
	url: string
	description?: string
	autoLoad?: boolean
	writable?: boolean
}

/**
 * Central manager for all GeoPackage databases
 * Handles downloading, caching, and lifecycle management
 */
export class GeoPackageManager {
	private packages = new Map<string, GeoPackageDatabase>()
	private configs = new Map<string, GeoPackageConfig>()
	private externalPackages = new Map<string, GeoPackageDatabase>() // External file system instances

	/**
	 * Register a GeoPackage configuration
	 */
	registerPackage(config: GeoPackageConfig): void {
		this.configs.set(config.id, config)
	}

	/**
	 * Load a GeoPackage by ID
	 * Downloads if not already cached
	 */
	async loadPackage(
		id: string,
		onProgress?: ProgressCallback
	): Promise<GeoPackageDatabase> {
		// Check if already loaded
		const existing = this.packages.get(id)
		if (existing) {
			await existing.waitForReady()
			return existing
		}

		// Get config
		const config = this.configs.get(id)
		if (!config) {
			throw new Error(`Unknown GeoPackage: ${id}`)
		}

		// Check if file exists in OPFS VFS directory
		const exists = await GeoPackageLoader.exists(config.filename)

		if (!exists) {
			await GeoPackageLoader.downloadWithStorageCheck(
				config.url,
				config.filename,
				onProgress
			)
		}

		// Create database instance with unique volume per package
		const { GeoPackageDatabase } = await import('./GeoPackageDatabase')
		const db = new GeoPackageDatabase(config.filename, `gpkg_${id}`)
		this.packages.set(id, db)

		// Wait for it to be ready
		await db.waitForReady()

		// Validate it
		const validation = await db.validate()
		if (!validation.valid) {
			throw new Error(`Invalid GeoPackage: ${validation.error}`)
		}

		return db
	}

	/**
	 * Get a loaded GeoPackage (doesn't download)
	 */
	getPackage(id: string): GeoPackageDatabase | null {
		return this.packages.get(id) || null
	}

	/**
	 * Unload a GeoPackage from memory
	 */
	async unloadPackage(id: string): Promise<void> {
		const db = this.packages.get(id)
		if (db) {
			await db.destroy()
			this.packages.delete(id)
		}
	}

	/**
	 * Delete a GeoPackage from OPFS and unload it
	 */
	async deletePackage(id: string): Promise<void> {
		await this.unloadPackage(id)

		const config = this.configs.get(id)
		if (config) {
			await GeoPackageLoader.delete(config.filename)
		}
	}

	/**
	 * Check if a package is cached in OPFS
	 */
	async isCached(id: string): Promise<boolean> {
		const config = this.configs.get(id)
		if (!config) return false
		return await GeoPackageLoader.exists(config.filename)
	}

	/**
	 * Get storage information
	 */
	async getStorageInfo(): Promise<{
		quota: number
		usage: number
		available: number
		percentUsed: number
		packages: Array<{ id: string; filename: string; size: number; cached: boolean }>
	}> {
		const storage = await GeoPackageLoader.checkStorage()
		const packages: Array<{ id: string; filename: string; size: number; cached: boolean }> = []

		for (const [id, config] of this.configs) {
			const cached = await GeoPackageLoader.exists(config.filename)
			const size = cached ? await GeoPackageLoader.getSize(config.filename) : 0

			packages.push({
				id,
				filename: config.filename,
				size,
				cached
			})
		}

		return {
			...storage,
			packages
		}
	}

	/**
	 * Cleanup all packages
	 */
	async cleanup(): Promise<void> {
		for (const id of this.packages.keys()) {
			await this.unloadPackage(id)
		}
	}

	/**
	 * List all registered packages
	 */
	listPackages(): GeoPackageConfig[] {
		return Array.from(this.configs.values())
	}

	/**
	 * Get all locally installed packages from the database
	 */
	async getInstalledPackages(): Promise<GeoPackageCatalog[]> {
		try {
			const packages = await GeoPackageCatalogTable.list()
			return packages
		} catch (error) {
			logger.error('Failed to get installed packages:', error)
			if (error instanceof Error) {
				logger.error('Error details:', error.message, error.stack)
			}
			return []
		}
	}

	/**
	 * Check if a specific package is installed locally
	 */
	async isPackageInstalled(packageId: string): Promise<boolean> {
		try {
			const pkg = await GeoPackageCatalogTable.get(packageId)
			return pkg !== null
		} catch (error) {
			logger.error('Failed to check package installation:', error)
			return false
		}
	}

	/**
	 * Get a specific installed package by ID
	 */
	async getInstalledPackage(packageId: string): Promise<GeoPackageCatalog | null> {
		try {
			return await GeoPackageCatalogTable.get(packageId)
		} catch (error) {
			logger.error('Failed to get installed package:', error)
			return null
		}
	}

	/**
	 * Get the local OPFS URL for an installed package
	 */
	getLocalPackageUrl(filename: string): string {
		// OPFS packages are accessed through the GeoPackageDatabase
		// which uses the filename to locate the file
		return `opfs://geopackages/${filename}`
	}

	/**
	 * Load an installed package from OPFS by its catalog entry
	 */
	async loadInstalledPackage(pkg: GeoPackageCatalog): Promise<GeoPackageDatabase> {
		// Check if already loaded
		const existing = this.packages.get(pkg.id)
		if (existing) {
			await existing.waitForReady()
			return existing
		}

		// Always verify file exists in OPFS
		// Even imported packages need the source file for the first VFS import
		const exists = await GeoPackageLoader.exists(pkg.filename)
		if (!exists) {
			throw new Error(`GeoPackage file not found in OPFS: ${pkg.filename}`)
		}

		// Create and load database with unique volume per package
		// If file is in VFS, it will open directly
		// If file is only in OPFS, it will be imported to VFS
		const { GeoPackageDatabase } = await import('./GeoPackageDatabase')
		const db = new GeoPackageDatabase(pkg.filename, `gpkg_${pkg.id}`, true)
		this.packages.set(pkg.id, db)

		try {
			await db.waitForReady()

			// Validate it
			const validation = await db.validate()
			if (!validation.valid) {
				logger.error(`GeoPackage validation failed: ${validation.error}`)
				this.packages.delete(pkg.id) // Remove failed package
				throw new Error(`Invalid GeoPackage: ${validation.error}`)
			}

			return db
		} catch (error) {
			// If initialization failed, clean up
			this.packages.delete(pkg.id)
			throw error
		}
	}

	/**
	 * Open a GeoPackage file from the external file system
	 * Uses File System Access API for persistent access
	 */
	async openExternalGeoPackage(): Promise<ExternalGeoPackageFile | null> {
		try {
			const fsManager = getFileSystemAccessManager()

			// Request file access with GeoPackage filter
			const handles = await fsManager.requestFileAccess({
				types: [{
					description: 'GeoPackage Files',
					accept: {
						'application/geopackage+sqlite3': ['.gpkg']
					}
				}],
				multiple: false
			})

			if (handles.length === 0) {
				return null
			}

			// Register the GeoPackage
			const geopackage = await fsManager.registerGeoPackageFile(handles[0].id)
			if (!geopackage) {
				throw new Error('Failed to register GeoPackage file')
			}

			logger.info(`Opened external GeoPackage: ${geopackage.name}`)
			return geopackage
		} catch (error) {
			logger.error('Failed to open external GeoPackage:', error)
			return null
		}
	}

	/**
	 * Load an external GeoPackage file into memory
	 * Copies the file to OPFS for SQLite access
	 */
	async loadExternalGeoPackage(geopackageId: string): Promise<GeoPackageDatabase> {
		// Check if already loaded
		const existing = this.externalPackages.get(geopackageId)
		if (existing) {
			await existing.waitForReady()
			return existing
		}

		const fsManager = getFileSystemAccessManager()
		const geopackage = await fsManager.getGeoPackage(geopackageId)
		if (!geopackage) {
			throw new Error(`External GeoPackage not found: ${geopackageId}`)
		}

		// Read the file data
		const data = await fsManager.readGeoPackageFile(geopackageId)
		if (!data) {
			throw new Error(`Failed to read external GeoPackage: ${geopackage.name}`)
		}

		// Copy to OPFS for SQLite access
		const filename = `external_${geopackage.name}`
		await GeoPackageLoader.saveToOPFS(filename, data)

		// Create database instance
		const { GeoPackageDatabase } = await import('./GeoPackageDatabase')
		const db = new GeoPackageDatabase(filename, `gpkg_external_${geopackageId}`, true)
		this.externalPackages.set(geopackageId, db)

		await db.waitForReady()

		// Validate it
		const validation = await db.validate()
		if (!validation.valid) {
			logger.error(`External GeoPackage validation failed: ${validation.error}`)
			throw new Error(`Invalid GeoPackage: ${validation.error}`)
		}

		logger.info(`Loaded external GeoPackage: ${geopackage.name}`)
		return db
	}

	/**
	 * Save changes to an external GeoPackage file
	 */
	async saveExternalGeoPackage(geopackageId: string): Promise<boolean> {
		const db = this.externalPackages.get(geopackageId)
		if (!db) {
			logger.error(`External GeoPackage not loaded: ${geopackageId}`)
			return false
		}

		try {
			// Export the database
			const data = await db.exportFile()

			// Write back to the external file
			const fsManager = getFileSystemAccessManager()
			const success = await fsManager.writeGeoPackageFile(geopackageId, data)

			if (success) {
				logger.info(`Saved external GeoPackage: ${geopackageId}`)
			}

			return success
		} catch (error) {
			logger.error('Failed to save external GeoPackage:', error)
			return false
		}
	}

	/**
	 * Unload an external GeoPackage
	 */
	async unloadExternalGeoPackage(geopackageId: string): Promise<void> {
		const db = this.externalPackages.get(geopackageId)
		if (db) {
			await db.destroy()
			this.externalPackages.delete(geopackageId)

			// Clean up OPFS copy
			const fsManager = getFileSystemAccessManager()
			const geopackage = await fsManager.getGeoPackage(geopackageId)
			if (geopackage) {
				const filename = `external_${geopackage.name}`
				await GeoPackageLoader.delete(filename)
			}
		}
	}

	/**
	 * Get all external GeoPackages
	 */
	async getExternalGeoPackages(): Promise<ExternalGeoPackageFile[]> {
		const fsManager = getFileSystemAccessManager()
		return await fsManager.getAllGeoPackages()
	}

	/**
	 * Remove an external GeoPackage reference
	 */
	async removeExternalGeoPackage(geopackageId: string): Promise<void> {
		// Unload if loaded
		await this.unloadExternalGeoPackage(geopackageId)

		// Remove from file system manager
		const fsManager = getFileSystemAccessManager()
		await fsManager.removeGeoPackage(geopackageId)

		logger.info(`Removed external GeoPackage: ${geopackageId}`)
	}

	/**
	 * Get an external GeoPackage database instance
	 */
	getExternalPackage(geopackageId: string): GeoPackageDatabase | null {
		return this.externalPackages.get(geopackageId) || null
	}
}

// Singleton instance
let managerInstance: GeoPackageManager | null = null

export function getGeoPackageManager(): GeoPackageManager {
	if (!managerInstance) {
		managerInstance = new GeoPackageManager()
	}
	return managerInstance
}

